import { IMarginerArticle, IOrder, IVehicleAccessory, IVehicleConfiguration, OrderCreationType } from 'next-common';
import { ICartPricesCustom } from '../../common';
import { ArticleType, IArticle, VatCode } from 'sales-types-common';
import {
  ICalculatePricesBasedOnArticleListResult,
  calculatePricesBasedOnArticleList,
  mapVehicleApplicationCodeStringToEnum,
} from 'sales-common-calculations';
import { defaultTo, isFinite } from 'lodash';

export const keyForOpsjoner = 'PACKAGE_AND_EQUIPMENTS';
export const retailerMarginPercentage = 15;

export const editableArticleTypes = [
  ArticleType.WINTERTIRES,
  ArticleType.ACCESSORY,
  ArticleType.IMPORTCONTRIBUTION,
  ArticleType.IMPORTCONTRIBUTION_PERCENTAGE,
  ArticleType.DELIVERY,
  ArticleType.SERVICECONTRACT,
  ArticleType.SALESPREPARATIONCOST,
];

export const getRetailerMarginValue = (accessory: IVehicleAccessory): number => {
  return accessory?.retailerMargin
    ? accessory?.retailerMargin
    : (accessory?.priceExcludingVat * retailerMarginPercentage) / 100;
};

export const calculateRetailerMargin = (accessory: IVehicleAccessory): number => {
  return accessory?.priceExcludingVat - getRetailerMarginValue(accessory);
};

export type MarginArticleWithPriorityAndVehiclePart = IMarginerArticle & {
  priority: number;
  vehiclePart: string;
};

export interface IMarginerSummary {
  salesTotal: number;
  vehicleDealerPriceExclVat: number;
  accessoriesSumPrice: number;
  marginAmount: number;
  marginPercentage: number;
}

export const getMarginerTabValues = (
  articles: Array<IMarginerArticle>,
  order: IOrder
): MarginArticleWithPriorityAndVehiclePart[] => {
  const preparedArticles: MarginArticleWithPriorityAndVehiclePart[] = [];

  articles?.forEach((article) => {
    const articleTypeName = article?.articleType?.toUpperCase();

    if (articleTypeName === ArticleType.VEHICLE) {
      preparedArticles.push({
        ...article,
        priority: 1,
        vehiclePart: 'Bil',
      });
    }
    if (articleTypeName === ArticleType.PACKAGE || articleTypeName === ArticleType.EQUIPMENT) {
      // Find that if "Opsjoner" is already added to "preparedArticles" array or not
      const isOpsjonerIndex = preparedArticles.findIndex((preArt) => preArt?.vehiclePart === 'Opsjoner');
      // If "Opsjoner" is already exist in "preparedArticles" than update the sum for article type "PACKAGE" and "EQUIPMENT"
      if (isOpsjonerIndex === -1) {
        preparedArticles.push({
          ...article,
          priority: 2,
          vehiclePart: 'Opsjoner',
          articleDescription: keyForOpsjoner,
        });
      } else {
        preparedArticles[isOpsjonerIndex].customerPriceExclVat += article.customerPriceExclVat ?? 0;
        preparedArticles[isOpsjonerIndex].dealerPriceExclVat += article.dealerPriceExclVat ?? 0;
      }
    }
    if (articleTypeName === ArticleType.ACCESSORY) {
      const accessoryName = article?.articleDescription
        ? (order?.accessories?.find((accessory) => article.articleDescription.includes(accessory?.customId))?.name ??
          '')
        : '';
      preparedArticles.push({
        ...article,
        priority: 3,
        vehiclePart: accessoryName,
      });
    }
    if (order?.lead?.orderType !== OrderCreationType.USED) {
      if (articleTypeName === ArticleType.IMPORTCONTRIBUTION) {
        preparedArticles.push({
          ...article,
          priority: 4,
          vehiclePart: 'Importør-rabatt',
        });
      }
      if (articleTypeName === ArticleType.IMPORTCONTRIBUTION_PERCENTAGE) {
        preparedArticles.push({
          ...article,
          priority: 4,
          vehiclePart: 'Importør-rabatt percentage',
        });
      }
    }
    if (articleTypeName === ArticleType.WINTERTIRES) {
      const winterTyreName =
        order?.accessories?.find((accessory) => accessory?.accessoryType?.toString() === 'TYRE')?.name ?? '';
      preparedArticles.push({
        ...article,
        priority: 5,
        vehiclePart: winterTyreName,
      });
    }
    if (order?.lead?.orderType === OrderCreationType.USED && articleTypeName === ArticleType.SALESPREPARATIONCOST) {
      preparedArticles.push({
        ...article,
        priority: 8,
        vehiclePart: 'Ekstra Påkost',
      });
    }
    if (order?.lead?.orderType !== OrderCreationType.USED && articleTypeName === ArticleType.DELIVERY) {
      preparedArticles.push({
        ...article,
        priority: 9,
        vehiclePart: 'Leveringsomkostninger',
      });
    }

    // Service contract has been removed from the margins // Don't Remove code
    // if (articleTypeName === ArticleType.SERVICECONTRACT) {
    //   preparedArticles.push({
    //     ...article,
    //     priority: 6,
    //     vehiclePart: 'Serviceavtale',
    //   });
    // }
    return preparedArticles;
  });

  const isImportContributionAvailable = preparedArticles?.find((article) =>
    [ArticleType.IMPORTCONTRIBUTION, ArticleType.IMPORTCONTRIBUTION_PERCENTAGE]?.includes(article?.articleType)
  );
  const isSalesPreparationCostAvailable = preparedArticles?.find((article) =>
    [ArticleType.SALESPREPARATIONCOST]?.includes(article?.articleType)
  );
  if (!isImportContributionAvailable && order?.lead?.orderType !== OrderCreationType.USED) {
    preparedArticles.push({
      id: null,
      articleType: ArticleType.IMPORTCONTRIBUTION,
      articleDescription: 'IMPORTCONTRIBUTION',
      customerPriceExclVat: 0,
      dealerPriceExclVat: 0,
      vatCode: VatCode.NOT_APPLICABLE.toString(),
      discountPercentage: 0,
      discountAmountExclVat: 0,
      priority: 4,
      vehiclePart: 'Importør-rabatt',
    });
  }

  if (!isSalesPreparationCostAvailable && order?.lead?.orderType === OrderCreationType.USED) {
    preparedArticles.push({
      id: null,
      articleType: ArticleType.SALESPREPARATIONCOST,
      articleDescription: 'SALESPREPARATIONCOST',
      customerPriceExclVat: 0,
      dealerPriceExclVat: 0,
      vatCode: VatCode.NOT_APPLICABLE.toString(),
      discountPercentage: 0,
      discountAmountExclVat: 0,
      priority: 8,
      vehiclePart: 'Ekstra Påkost',
    });
  }

  return preparedArticles;
};

export const getDiscountDeliveryMarginerTabValues = (
  finalPrice: ICartPricesCustom,
  order: IOrder
): MarginArticleWithPriorityAndVehiclePart[] => {
  const discountArticles: MarginArticleWithPriorityAndVehiclePart[] = [];
  if (!!finalPrice && !finalPrice?.error && order?.lead?.orderType !== OrderCreationType.USED) {
    discountArticles.push({
      id: null,
      articleId: null,
      articleType: ArticleType.DISCOUNT_AMOUNT,
      priority: 7,
      vehiclePart: 'Rabatt',
      customerPriceExclVat: -Math.abs(finalPrice?.calculatedSumOfDiscountsExclVat ?? 0),
      dealerPriceExclVat: NaN,
      articleDescription: 'discountRabbat',
      adjustedDealerPriceExclVat: null,
      adjustedByName: null,
      adjustedTimestamp: null,
    });
  }

  return discountArticles;
};

export const prepareArticlesFromArticleList = (articleList: IMarginerArticle[]): IArticle[] => {
  return articleList?.length
    ? articleList
        ?.map(
          (article) =>
            ({
              priceExclVat: article?.customerPriceExclVat,
              dealerPriceExclVat: getActualPriceConsideredForKjøp(article),
              vatCode: article?.vatCode as VatCode,
              articleType: article?.articleType,
              discountAmountExclVat: article.discountAmountExclVat ?? 0,
              discountPercentage: article.discountPercentage ?? 0,
              key: article?.articleDescription,
            }) as IArticle
        )
        ?.filter((item) => item?.articleType !== ArticleType.SERVICECONTRACT)
    : [];
};

export const mapMarginerSummary = (order: IOrder, vehicleConfig: IVehicleConfiguration): IMarginerSummary => {
  const articles = prepareArticlesFromArticleList(order?.articleList)?.map((article) => {
    if (order?.lead?.orderType === OrderCreationType.USED && article.articleType === ArticleType.DELIVERY) {
      // Remove delivery charges for used car orders
      article.priceExclVat = 0;
      article.dealerPriceExclVat = 0;
    }
    if (
      article?.articleType === ArticleType.IMPORTCONTRIBUTION ||
      article?.articleType === ArticleType.IMPORTCONTRIBUTION_PERCENTAGE ||
      article.articleType === ArticleType.SALESPREPARATIONCOST
    ) {
      const matchingOrderArticle = order?.articleList?.find(
        (orderArt) => orderArt?.articleType === article.articleType
      );
      const { adjustedDealerPriceExclVat } = matchingOrderArticle || {};
      if (adjustedDealerPriceExclVat != null && adjustedDealerPriceExclVat !== undefined) {
        article.priceExclVat = adjustedDealerPriceExclVat;
        article.dealerPriceExclVat = adjustedDealerPriceExclVat;
      }
    }
    return article;
  });
  const vehicleApplicationCode =
    order?.pimSnapshot?.vehicleUsageCode ?? order?.pimSnapshot?.vehicleType ?? vehicleConfig?.model?.vehicleType ?? '';

  if (!articles?.length) return null;
  console.log(
    'Articles Input for Marginer Oppsummering: ',
    ' vehicleApplicationCode: ',
    vehicleApplicationCode,
    articles
  );
  const result: ICalculatePricesBasedOnArticleListResult = calculatePricesBasedOnArticleList({
    articles: articles,
    vehicleApplicationCode: mapVehicleApplicationCodeStringToEnum(vehicleApplicationCode),
  });
  console.log('Response for Marginer Oppsummering ', result);
  return {
    salesTotal: result?.indicativePriceExclVatExclTax - result?.calculatedSumOfDiscountsExclVat,
    vehicleDealerPriceExclVat: defaultTo(result?.sumDealerPriceVehicleOptionsExclImporterDiscount, 0),
    accessoriesSumPrice: result?.sumDealerPricePackageEquipmentWintertiresAccessories ?? 0,
    marginAmount: result?.postRecalculationMarginAmount ?? 0,
    marginPercentage: (result?.postRecalculationMarginPercentage ?? 0) * 100,
  };
};

export const mapSummaryAccordionKey = (key: string): string => {
  switch (key) {
    case 'salesTotal':
      return 'Salg totalt';
    case 'vehicleDealerPriceExclVat':
      return 'Innkjøp bil';
    case 'accessoriesSumPrice':
      return 'Påkost';
    case 'marginAmount':
      return 'Fortjeneste';
    case 'marginPercentage':
      return 'Fortjeneste i prosent';
    default:
      return '';
  }
};

export const getActualPriceConsideredForKjøp = (article: IMarginerArticle): number => {
  const originalPriceForKjøp = getOriginalPriceConsideredForKjøp(article);
  const isPriceAdjusted = isFinite(article.adjustedDealerPriceExclVat);
  const convertedAdjustedPrice = isPriceAdjusted ? article.adjustedDealerPriceExclVat : null;
  const actualPriceConsideredForKjøp = isPriceAdjusted ? convertedAdjustedPrice : originalPriceForKjøp;

  return actualPriceConsideredForKjøp;
};

export const getOriginalPriceConsideredForKjøp = (article: IMarginerArticle): number =>
  article.articleType === ArticleType.IMPORTCONTRIBUTION_PERCENTAGE
    ? article.customerPriceExclVat
    : article.dealerPriceExclVat;
